import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function StrategyDesigner() {
  const { t, i18n } = useTranslation();

  const [modelParams, setModelParams] = useState({
    scenarioName: "Aggressive Merchant '26",
    capex: 3500,
    capacityMarket: 260,
    volatilityMultiplier: 1.4
  });

  const [results] = useState({
    projectIrr: 18.4,
    irrChange: 4.2,
    npv: 42.5,
    paybackPeriod: 5.2
  });

  // Mock data - w prawdziwej aplikacji to by przyszło z API po uruchomieniu symulacji
  const cashflowData = [
    { year: '2024', aggressive: -65, base: -65 },
    { year: '2025', aggressive: -58, base: -60 },
    { year: '2026', aggressive: -45, base: -52 },
    { year: '2027', aggressive: -38, base: -45 },
    { year: '2028', aggressive: -25, base: -35 },
    { year: '2029', aggressive: -15, base: -25 },
    { year: '2030', aggressive: -2, base: -12 },
    { year: '2031', aggressive: 8, base: 2 },
    { year: '2032', aggressive: 22, base: 12 },
    { year: '2033', aggressive: 35, base: 20 },
    { year: '2034', aggressive: 50, base: 28 },
    { year: '2035', aggressive: 68, base: 35 },
    { year: '2036', aggressive: 85, base: 42 },
    { year: '2037', aggressive: 100, base: 48 },
    { year: '2038', aggressive: 112, base: 52 },
    { year: '2039', aggressive: 122, base: 55 },
  ];

  const savedScenarios = [
    { id: 1, name: 'Aggressive Merchant', irr: 18.4, active: true },
    { id: 2, name: 'Conservative CM only', irr: 9.2, active: false },
    { id: 3, name: 'Base Case 2025', irr: 14.1, active: false },
  ];

  const handleSliderChange = (param, value) => {
    setModelParams(prev => ({ ...prev, [param]: value }));
  };

  const handleRunSimulation = () => {
    // W prawdziwej aplikacji tutaj byłoby wywołanie API
    console.log('Running simulation with params:', modelParams);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 mb-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-xs text-gray-600">{entry.name}</span>
              </div>
              <span className="text-xs font-medium text-gray-900">{entry.value}M PLN</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('strategyDesigner.title')}</h1>
        <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
          {t('strategyDesigner.subtitle')}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Project IRR */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            {t('strategyDesigner.kpi.projectIrr')}
          </p>
          <div className="flex items-baseline space-x-2 mb-1">
            <span className="text-3xl font-bold text-blue-600">{results.projectIrr}%</span>
          </div>
          <div className="flex items-center space-x-1 text-xs">
            <TrendingUp className="w-3 h-3 text-green-600" />
            <span className="text-green-600 font-medium">+{results.irrChange}%</span>
            <span className="text-gray-500">{t('strategyDesigner.kpi.vsBase')}</span>
          </div>
        </div>

        {/* NPV */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            {t('strategyDesigner.kpi.npv')}
          </p>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-gray-900">{formatCurrency(results.npv)}M</span>
            <span className="text-xs text-gray-600">PLN</span>
          </div>
        </div>

        {/* Payback Period */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            {t('strategyDesigner.kpi.paybackPeriod')}
          </p>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-gray-900">{formatCurrency(results.paybackPeriod)}</span>
            <span className="text-xs text-gray-600">{t('strategyDesigner.kpi.years')}</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Panel - Model Parameters */}
        <div className="space-y-6">
          {/* Model Parameters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-4">
              {t('strategyDesigner.modelParams.title')}
            </h3>

            {/* Scenario Name */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">
                {t('strategyDesigner.modelParams.scenarioName')}
              </label>
              <input
                type="text"
                value={modelParams.scenarioName}
                onChange={(e) => setModelParams(prev => ({ ...prev, scenarioName: e.target.value }))}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* CAPEX Slider */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">
                {t('strategyDesigner.modelParams.capex')}
              </label>
              <div className="text-base font-bold text-gray-900 mb-1">
                {modelParams.capex.toLocaleString(i18n.language)}
              </div>
              <input
                type="range"
                min="2000"
                max="5000"
                step="100"
                value={modelParams.capex}
                onChange={(e) => handleSliderChange('capex', Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            {/* Capacity Market Slider */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">
                {t('strategyDesigner.modelParams.capacityMarket')}
              </label>
              <div className="text-base font-bold text-gray-900 mb-1">
                {modelParams.capacityMarket}
              </div>
              <input
                type="range"
                min="100"
                max="500"
                step="10"
                value={modelParams.capacityMarket}
                onChange={(e) => handleSliderChange('capacityMarket', Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            {/* Volatility Multiplier Slider */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">
                {t('strategyDesigner.modelParams.volatilityMultiplier')}
              </label>
              <div className="text-base font-bold text-gray-900 mb-1">
                {modelParams.volatilityMultiplier.toFixed(1)}x
              </div>
              <input
                type="range"
                min="0.5"
                max="3.0"
                step="0.1"
                value={modelParams.volatilityMultiplier}
                onChange={(e) => handleSliderChange('volatilityMultiplier', Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            {/* Run Simulation Button */}
            <button
              onClick={handleRunSimulation}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Play className="w-4 h-4" />
              <span>{t('strategyDesigner.modelParams.runSimulation')}</span>
            </button>
          </div>

          {/* Saved Scenarios */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3">
              {t('strategyDesigner.savedScenarios.title')}
            </h3>

            <div className="space-y-2">
              {savedScenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  className={`w-full text-left px-2 py-2 rounded transition-colors ${
                    scenario.active
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-white border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${scenario.active ? 'text-blue-700' : 'text-gray-700'}`}>
                      {scenario.name}
                    </span>
                    <span className={`text-xs font-bold ${scenario.active ? 'text-blue-700' : 'text-gray-900'}`}>
                      {scenario.irr}% IRR
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Cashflow Chart */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-6 self-start">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('strategyDesigner.chart.title')}
            </h2>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cashflowData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="year" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  interval={1}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  tickFormatter={(value) => `${value}M PLN`}
                  domain={[-80, 140]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom"
                  height={36}
                  iconType="line"
                  formatter={(value) => {
                    if (value === 'aggressive') return t('strategyDesigner.chart.aggressiveScenario');
                    if (value === 'base') return t('strategyDesigner.chart.baseCase');
                    return value;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="aggressive" 
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#3b82f6' }}
                  name="aggressive"
                  strokeDasharray={null}
                />
                <Line 
                  type="monotone" 
                  dataKey="base" 
                  stroke="#9ca3af"
                  strokeWidth={2}
                  dot={false}
                  name="base"
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StrategyDesigner;

